import {
  BulkCreateShiftmanagementShiftShiftsInput,
  ShiftManagementEnumsShiftStateEnum,
} from 'generated/resources/graphql'
import { flatMap, uniq } from 'lodash'
import type moment from 'moment'

import { convertDate, dateToString, stringToDateWithTimezone } from 'modules/core/lib/dates'

import type { ShiftInputsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'

import { View } from './types'

/**
 * Given an employee and a list of days, build the parameters for the shifts we
 * want to create on each one of the days.
 */
const buildShiftParameters = (
  defaultAttributesByDate: ShiftInputsAttributesByDate,
  name: string | null,
  startTime: moment.Moment,
  endTime: moment.Moment,
  isMultiDayShift: boolean,
  employeeId: string | null,
  days: moment.Moment[],
  extraHours: boolean,
  notes: string | null,
  locationId: string | null,
  workAreaId: string | null,
  defaultShiftId: string | null,
  color: string | null,
  templateWeekId: string | null,
  unassignedShiftId: string | null,
  state: ShiftManagementEnumsShiftStateEnum | null,
  leaveId: string | undefined,
  companyId: string
): BulkCreateShiftmanagementShiftShiftsInput[] => {
  return days.flatMap((startDay: moment.Moment): BulkCreateShiftmanagementShiftShiftsInput[] => {
    const endDay = isMultiDayShift ? startDay.clone().add(1, 'day') : startDay.clone()

    const startAt = startDay.clone().hour(startTime.hour()).minute(startTime.minute())

    const endAt = endDay.clone().hour(endTime.hour()).minute(endTime.minute())

    const date = dateToString(startAt, 'backend')
    const defaultAttributesArray = defaultAttributesByDate[date] || []

    const attributes: BulkCreateShiftmanagementShiftShiftsInput = {
      employeeId: employeeId ? String(employeeId) : null,
      name,
      startAt: dateToString(startAt, 'backend_datetime'),
      endAt: dateToString(endAt, 'backend_datetime'),
      extraHours: extraHours,
      notes,
      locationId: locationId ? String(locationId) : undefined,
      workAreaId: workAreaId ? String(workAreaId) : undefined,
      defaultShiftId: defaultShiftId ? String(defaultShiftId) : undefined,
      templateWeekId,
      unassignedShiftId,
      state,
      leaveId: leaveId ? String(leaveId) : undefined,
      companyId,
    }

    if (!defaultAttributesArray?.length) {
      return [{ ...attributes }]
    }

    return defaultAttributesArray.map((defaultAttributes) => {
      return { ...attributes, ...defaultAttributes }
    })
  })
}

export interface BuildAddShiftsParametersProps {
  view: View
  hasSelection: boolean
  defaultAttributesByDate: ShiftInputsAttributesByDate
  name: string | null
  unparsedStartTime: string | null | undefined
  unparsedEndTime: string | null | undefined
  daysByRow: Record<string, string[]>
  defaultShiftId: string | null | undefined
  extraHours: boolean
  notes: string | null
  locationId: string | null
  workAreaId: string | null
  color: string | null
  templateWeekId: string | null | undefined
  unassignedShiftId: string | null
  state: ShiftManagementEnumsShiftStateEnum | null
  leaveId: string | undefined
  companyId: string
  employeeSelection?: boolean
}

/**
 * Builds the map parameters to be sent when bulk creating shifts for multiple
 * employees on multiple days an  with the same start/end times.
 *
 * @param {string} unparsedStartTime the time at what the shift starts in the
 *   format 'HH:MM'
 * @param {string} unparsedEnd the time at what the shift ends in the format
 *   'HH:MM'
 * @param {{[key: string]: Array<string>}} daysByRow a map of the days
 *   in which we're adding shifts to each employee. The list of days is an
 *   array with using `BACKEND` format (check `lib/dates`)
 * @returns {Array<ShiftParams>}
 */
// Optional parameters: notes?, workAreaId?, locationId?, extraHours?
export const buildAddShiftsInputParameters = ({
  view,
  hasSelection,
  defaultAttributesByDate,
  name,
  unparsedStartTime,
  unparsedEndTime,
  daysByRow,
  defaultShiftId = null,
  extraHours = false,
  notes = null,
  locationId = null,
  workAreaId = null,
  color = null,
  templateWeekId = null,
  unassignedShiftId = null,
  state = null,
  leaveId = undefined,
  companyId,
}: BuildAddShiftsParametersProps): BulkCreateShiftmanagementShiftShiftsInput[] => {
  if (!unparsedStartTime || !unparsedEndTime) return []

  const startTime = stringToDateWithTimezone(unparsedStartTime, 'ui_time', 'Etc/UTC')
  const endTime = stringToDateWithTimezone(unparsedEndTime, 'ui_time', 'Etc/UTC')

  if (!startTime || !startTime.isValid()) return []
  if (!endTime || !endTime.isValid()) return []

  const isMultiDayShift = endTime.isSameOrBefore(startTime)
  const format = hasSelection ? view.dateFormat : 'backend'

  // When employeeSelection is active, skip employee iteration and only iterate over dates
  // The employee assignment is handled by the backend
  if (!hasSelection) {
    const allDays = Object.values(daysByRow).flat()
    const dates = uniq(allDays.map((day) => convertDate(day, format, 'backend')))
    const employeeDates = dates.map((day: string) =>
      stringToDateWithTimezone(day, 'backend', 'Etc/UTC')
    )

    return buildShiftParameters(
      defaultAttributesByDate,
      name,
      startTime,
      endTime,
      isMultiDayShift,
      null, // employeeId is handled by backend when employeeSelection is active
      employeeDates,
      extraHours,
      notes,
      locationId,
      workAreaId,
      defaultShiftId,
      color,
      templateWeekId,
      unassignedShiftId,
      state,
      leaveId,
      companyId
    )
  }

  // Original behavior: iterate over employees and their respective days
  const employeeIds = Object.keys(daysByRow)
  return flatMap(employeeIds, (employeeId: string) => {
    const selectedDays = daysByRow[employeeId] ?? []
    // Cast the date to backend format to avoid duplicates in daily view
    const dates = uniq(selectedDays.map((day) => convertDate(day, format, 'backend')))
    const employeeDates = dates.map((day: string) =>
      stringToDateWithTimezone(day, 'backend', 'Etc/UTC')
    )

    return buildShiftParameters(
      defaultAttributesByDate,
      name,
      startTime,
      endTime,
      isMultiDayShift,
      employeeId,
      employeeDates,
      extraHours,
      notes,
      locationId,
      workAreaId,
      defaultShiftId,
      color,
      templateWeekId,
      unassignedShiftId,
      state,
      leaveId,
      companyId
    )
  })
}
